import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiInstance from '@/utils/api'
import { useCountriesStore } from './countries'

/**
 * The export was refused because the postings do not reconcile — a missing
 * revenue account for a VAT rate in use, a non-numeric account number, and so
 * on. `problems` lists exactly what to fix.
 */
export class DatevExportInvalidError extends Error {
  constructor(
    message: string,
    public readonly problems: string[],
  ) {
    super(message)
    this.name = 'DatevExportInvalidError'
  }
}

export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface Manager {
  name: string | null
  email: string | null
  mobile_no: string | null
}

export interface Company {
  id?: string | number
  full_name: string | null
  company_name: string | null
  email: string | null
}

export interface Partner {
  full_name: string | null
  company_name: string | null
  email: string | null
}

export interface VatRate {
  vat_rate_1?: number
  vat_rate_2?: number
}

export interface Language {
  name: string
  code: string
  flag: string
}

export interface OpeningHourSlot {
  from: string
  to: string
}

export type OpeningHours = Record<string, OpeningHourSlot[]>

export interface RestaurantSetting {
  id: number
  currency: string
  restaurant_name: string
  address: string
  slug: string
  default_lang: string
  banner_image: string
  logo: string
  dark_mode: number
  app_color: string
  vat_rates: VatRate[]
  is_languages_active: number
  languages: Language[]
  closing_days: string[]
  opening_hours: OpeningHours
}

export interface RestaurantLicense {
  id: string
  license_type: string
  billing_period: string
  status: string
  purchased_at: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  cancelled_at: string | null
  license_key_source?: string | null
  origin?: string
  kiosk: { id: number; name: string } | null
}

export interface Restaurant {
  id: string | number
  name: string
  address: string
  restaurantBanner?: string
  manager?: Manager
  company?: Company
  partner?: Partner
  status: number
  phone?: string
  email?: string
  city?: string
  country?: string
  cuisineType?: string
  createdAt?: string
  redirectToOutlet?: boolean
  redirectUrl?: string
  customizedTiming?: boolean
  redirectTimings?: Record<string, TimeSlot[]>
  urlSuffix?: string
  defaultLanguage?: 'en' | 'de'
  returnUrl?: string
  partnerId?: string
  companyId?: string
  zipcode?: string
  currency?: string
  managerName?: string
  managerEmail?: string
  managerMobileNo?: string
  activate_datev_exp?: number
  interfaceOption?: string | null
  has_active_license?: boolean
  /** End of the running free trial (per restaurant), null when not in trial */
  trial_ends_at?: string | null
  licenses?: RestaurantLicense[]
}

export const useRestaurantsStore = defineStore('restaurants', () => {
  const restaurants = ref<Restaurant[]>([])

  const isLoaded = ref(false)

  const restaurantSetting = ref<RestaurantSetting | null>(null)

  const searchQuery = ref('')
  const selectedPartner = ref('')
  const selectedCompany = ref('')
  /** '' = all, otherwise only restaurants with that status. Archived restaurants are the inactive ones. */
  const selectedStatus = ref<'' | 'active' | 'inactive'>('active')

  const addRestaurant = (restaurant: Omit<Restaurant, 'id' | 'createdAt'>, id?: string | number) => {
    const newRestaurant: Restaurant = {
      ...restaurant,
      id: id !== undefined ? id.toString() : Date.now().toString(),
      createdAt: new Date().toISOString().split('T')[0],
    }
    restaurants.value.push(newRestaurant)
    return newRestaurant
  }

  const updateRestaurant = (id: string, updates: Partial<Restaurant>) => {
    const index = restaurants.value.findIndex((r) => r.id === id)
    if (index !== -1) {
      restaurants.value[index] = { ...restaurants.value[index], ...updates }
    }
  }

  const deleteRestaurant = async (id: string): Promise<void> => {
    try {
      await apiInstance.delete(`/delete-restaurant/${id}`)
      restaurants.value = restaurants.value.filter((r) => r.id !== id)
    } catch (error: any) {
      throw new Error(error.response?.data?.message || `Failed to delete restaurant (${error.response?.status})`)
    }
  }

  const getRestaurant = (id: string) => {
    return restaurants.value.find((r) => r.id === id)
  }

  const fetchRestaurants = async () => {
    try {
      // Skip if already loaded
      if (isLoaded.value) {
        // return
      }

      const response = await apiInstance.get('/get-restaurants')
      const data = response.data

      if (data.error === true) {
        console.warn('API error:', data.message, 'Using default data.')
        return
      }

      if (!data.data || !Array.isArray(data.data)) {
        console.warn('Invalid response structure. Using default data.')
        return
      }

      const fetchedRestaurants = data.data.map((apiRestaurant: any) => ({
        id: apiRestaurant.id.toString(),
        name: apiRestaurant.name,
        address: apiRestaurant.address,
        restaurantBanner: apiRestaurant.restaurant_banner,
        manager: apiRestaurant.manager || {
          name: null,
          email: null,
          mobile_no: null,
        },
        company: apiRestaurant.company || {
          full_name: null,
          company_name: null,
          email: null,
        },
        partner: apiRestaurant.partner || {
          full_name: null,
          company_name: null,
          email: null,
        },
        status: apiRestaurant.status || 0,
        createdAt: new Date().toISOString().split('T')[0],
        companyId: apiRestaurant.company?.id !== undefined && apiRestaurant.company?.id !== null
          ? apiRestaurant.company.id.toString()
          : undefined,
        activate_datev_exp: apiRestaurant.activate_datev_exp || 0,
        interfaceOption: apiRestaurant.interface_option ?? null,
        licenses: Array.isArray(apiRestaurant.licenses) ? apiRestaurant.licenses : [],
        has_active_license: Array.isArray(apiRestaurant.licenses)
          ? apiRestaurant.licenses.some((l: any) => l.status === 'active')
          : false,
        trial_ends_at: apiRestaurant.trial_ends_at ?? null,
      }))

      restaurants.value = fetchedRestaurants
      isLoaded.value = true
    } catch (error) {
      console.warn('Fetch restaurants error:', error, 'Using default data.')
    }
  }

  const fetchRestaurantBasicInfo = async (restaurantId: string): Promise<any> => {
    try {
      const response = await apiInstance.get(`/restaurant/restaurant-info/${restaurantId}`)

      if (response.data.error === false && response.data.data) {
        const apiData = response.data.data

        // Transform outlet_timings from ["HH:MM-HH:MM"] format to our { startTime, endTime } format
        const redirectTimings: Record<string, any[]> = {}
        if (apiData.outlet_timings && Array.isArray(apiData.outlet_timings)) {
          apiData.outlet_timings.forEach((timing: any) => {
            const day = timing.week_day
            redirectTimings[day] = timing.available_timings.map((slot: string) => {
              const [startTime, endTime] = slot.split('-')
              return { startTime, endTime }
            })
          })
        }

        // Currency code to symbol mapping
        const currencyMap: { [key: string]: string } = {
          'EUR': '€',
          'USD': '$',
          'GBP': '£',
        }
        const currencySymbol = currencyMap[apiData.currency_code] || apiData.currency_code || ''

        // Get or create restaurant in store
        let existingRestaurant = getRestaurant(restaurantId)

        if (!existingRestaurant) {
          // Create the restaurant if it doesn't exist
          existingRestaurant = {
            id: restaurantId,
            name: apiData.restaurant_name || 'Restaurant',
            address: apiData.street || '',
            phone: apiData.manager_mobile_no || '',
            email: apiData.manager_email || '',
            city: apiData.city || '',
            country: apiData.country_id?.toString() || '',
            cuisineType: 'Unknown',
            status: 1,
            createdAt: new Date().toISOString().split('T')[0],
          }
          restaurants.value.push(existingRestaurant)
        }

        // Build the updated data
        const updatedData: Partial<Restaurant> = {
          name: apiData.restaurant_name || existingRestaurant.name,
          address: apiData.street || existingRestaurant.address,
          city: apiData.city || existingRestaurant.city,
          zipcode: apiData.zip_code || '',
          country: apiData.country_id?.toString() || existingRestaurant.country,
          currency: apiData.hide_currency ? '' : currencySymbol || '',
          email: apiData.manager_email || existingRestaurant.email,
          phone: apiData.manager_mobile_no || existingRestaurant.phone,
          managerName: apiData.manager_name || '',
          managerEmail: apiData.manager_email || '',
          managerMobileNo: apiData.manager_mobile_no || '',
          partnerId: apiData.partner_id?.toString() || '',
          companyId: apiData.company_id?.toString() || '',
          urlSuffix: apiData.webpage_link || '',
          defaultLanguage: (apiData.default_lang as 'en' | 'de') || 'en',
          returnUrl: apiData.redirect_return_url || '',
          redirectToOutlet: apiData.redirect_outlet_active === 1,
          redirectUrl: apiData.outlet_url_link || '',
          customizedTiming: apiData.outlet_customize_active === 1,
          redirectTimings,
          restaurantBanner: apiData.restaurant_banner || '',
          activate_datev_exp: apiData.activate_datev_exp || 0,
        }

        updateRestaurant(restaurantId, updatedData)
        return apiData
      } else {
        throw new Error(response.data.message || 'Failed to fetch restaurant info')
      }
    } catch (error: any) {
      console.error('Error fetching restaurant info:', error)
      throw new Error(error.response?.data?.message || error.message || 'Network error while fetching restaurant info')
    }
  }

  const fetchRestaurantSetting = async (slug: string): Promise<RestaurantSetting> => {
    try {
      const response = await apiInstance.get(
        `/restaurant/settings/${slug}`
      )

      if (response.data?.error) {
        throw new Error(response.data.message || 'Failed to fetch restaurant settings')
      }

      restaurantSetting.value = response.data.data as RestaurantSetting
      return restaurantSetting.value
    } catch (error: any) {
      console.error('Error fetching restaurant setting:', error)
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Network error while fetching restaurant setting'
      )
    }
  }


  // The API answers with the CSV itself on success and with the usual JSON
  // envelope on failure, so the request asks for a Blob and decodes errors.
  const readBlobJson = async (data: unknown): Promise<any> => {
    if (!(data instanceof Blob)) return data
    try {
      return JSON.parse(await data.text())
    } catch {
      return null
    }
  }

  const toDatevExportError = (payload: any, fallback: string): Error => {
    // The API refuses to write a stapel whose postings do not add up, and
    // returns 422 with the specific reasons. Those reasons are the whole point
    // — surface them rather than collapsing to a single message.
    const problems = payload?.data?.problems
    const headline = payload?.message || fallback

    if (Array.isArray(problems) && problems.length > 0) {
      return new DatevExportInvalidError(headline, problems)
    }

    return new Error([headline, payload?.data?.reason].filter(Boolean).join(' '))
  }

  /** File name the API gave in Content-Disposition (DATEV requires its own EXTF_*.csv name), or null. */
  const filenameFromDisposition = (header: unknown): string | null => {
    if (typeof header !== 'string') return null
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
    if (!match) return null
    try {
      return decodeURIComponent(match[1].trim())
    } catch {
      return match[1].trim()
    }
  }

  // One file for the whole company: the API merges its DATEV-enabled restaurants.
  const exportDatev = async (
    companyId: string,
    dateFrom: string,
    dateTo: string
  ): Promise<{ blob: Blob; filename: string | null }> => {
    let response
    try {
      response = await apiInstance.post(
        `/companies/${companyId}/datev-export`,
        {
          date_from: dateFrom,
          date_to: dateTo,
        },
        { responseType: 'blob' }
      )
    } catch (error: any) {
      const payload = await readBlobJson(error.response?.data)
      if (payload) {
        throw toDatevExportError(payload, 'DATEV export was not generated')
      }
      throw new Error(error.message || 'Failed to export DATEV file')
    }

    const contentType = response.headers['content-type'] || ''
    if (contentType.includes('application/json')) {
      const payload = await readBlobJson(response.data)
      throw toDatevExportError(payload, 'Failed to generate DATEV export')
    }

    return {
      blob: response.data as Blob,
      filename: filenameFromDisposition(response.headers['content-disposition']),
    }
  }

  const updateRestaurantStatus = async (restaurantId: string, status: number): Promise<void> => {
    try {
      const response = await apiInstance.post(`/restaurant/${restaurantId}/status`, { status })
      if (response.data.error === true) {
        throw new Error(response.data.message || 'Failed to update restaurant status')
      }
      updateRestaurant(restaurantId, { status })
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to update restaurant status')
    }
  }

  const getRestaurantStats = async (restaurantId: string, startDate: string, endDate: string): Promise<any> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/restaurant-stats`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      })

      if (response.data.error === false && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to fetch restaurant stats')
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch restaurant stats')
    }
  }

  const getRestaurantSetupStatus = async (restaurantId: string): Promise<any> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/restaurant-setup-status`)

      if (response.data.error === false && response.data.data) {
        return response.data.data
      }

      throw new Error(response.data.message || 'Failed to fetch restaurant setup status')
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch restaurant setup status')
    }
  }

  const reset = () => {
    restaurants.value = []
    restaurantSetting.value = null
    isLoaded.value = false
    searchQuery.value = ''
    selectedPartner.value = ''
    selectedCompany.value = ''
    selectedStatus.value = 'active'
  }

  return {
    restaurants,
    restaurantSetting,
    isLoaded,
    searchQuery,
    selectedPartner,
    selectedCompany,
    selectedStatus,
    addRestaurant,
    updateRestaurant,
    deleteRestaurant,
    getRestaurant,
    fetchRestaurants,
    fetchRestaurantBasicInfo,
    fetchRestaurantSetting,
    exportDatev,
    updateRestaurantStatus,
    getRestaurantStats,
    getRestaurantSetupStatus,
    reset,
  }
})
